// ============================================
// @narbodica/auth — NarbodicaAuth.jsx
// ============================================
// Single auth component shared across all
// Narbodica apps. Handles login, register,
// MFA, forgot password, and passkey flows.
//
// Usage:
//   import { NarbodicaAuth } from '@narbodica/auth';
//
//   <NarbodicaAuth
//     app="spark"                    // 'maia' | 'spark' | 'connect' | 'eros' | 'artemis'
//     appLabel="Spark"               // Display name
//     appTagline="Find your person"  // Shown under logo
//     accentColor="#8b5cf6"          // App accent colour
//     apiBase={API_BASE}             // From app's src/utils/apiBase.js or src/apiConfig.js
//     onSuccess={(tokens, user) => {}} // Called after successful auth
//     defaultMode="login"            // 'login' | 'register'
//   />
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { checkPasskeySupport, loginWithPasskey, registerPasskey } from '../utils/passkey.js';
import { checkPasswordStrength, checkPasswordBreach } from '../utils/password.js';

const API = (base, path) => `${base}/api${path}`;

// ─── Styles ─────────────────────────────────────────────────────────────────
// CSS-var based so they work in both Tailwind and non-Tailwind apps.
// Each app sets --narb-accent to its own colour.
const S = {
  wrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100%', padding: '32px 24px',
    gap: '20px', fontFamily: "'Inter', sans-serif",
  },
  logo: (accent) => ({
    fontFamily: "var(--font-serif)",
    fontSize: 40, fontWeight: 400,
    background: `linear-gradient(135deg, ${accent}, ${accent}99)`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text', margin: 0,
  }),
  tagline: {
    color: '#71717a', fontSize: 14, margin: 0, textAlign: 'center',
  },
  tabs: {
    display: 'flex', width: '100%', borderBottom: '1px solid #27272a',
    marginBottom: 4,
  },
  tab: (active, accent) => ({
    flex: 1, padding: '10px 0', background: 'none', border: 'none',
    borderBottom: `2px solid ${active ? accent : 'transparent'}`,
    color: active ? accent : '#71717a',
    fontSize: 14, fontWeight: active ? 600 : 400,
    cursor: 'pointer', transition: 'all 0.2s',
    fontFamily: "'Inter', sans-serif",
  }),
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: 10 },
  inputWrap: { position: 'relative', width: '100%' },
  input: (accent) => ({
    width: '100%', padding: '13px 16px',
    background: 'rgba(24,24,27,0.6)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, color: '#fafafa',
    fontSize: 14, outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    fontFamily: "'Inter', sans-serif",
  }),
  inputFocus: (accent) => ({
    borderColor: `${accent}66`,
  }),
  select: (accent) => ({
    width: '100%', padding: '13px 16px',
    background: 'rgba(24,24,27,0.6)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, color: '#fafafa',
    fontSize: 14, outline: 'none',
    cursor: 'pointer', appearance: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Inter', sans-serif",
  }),
  btn: (accent, disabled) => ({
    width: '100%', padding: '14px 0',
    background: disabled ? '#3f3f46' : accent,
    border: 'none', borderRadius: 12,
    color: disabled ? '#71717a' : '#fff',
    fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.2s, background 0.2s',
    fontFamily: "'Inter', sans-serif",
    opacity: disabled ? 0.6 : 1,
  }),
  btnGhost: {
    background: 'none', border: '1px solid #27272a', borderRadius: 12,
    width: '100%', padding: '14px 0', color: '#a1a1aa',
    fontSize: 14, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: 8,
    fontFamily: "'Inter', sans-serif",
  },
  error: {
    color: '#f87171', fontSize: 12, textAlign: 'center',
    background: 'rgba(239,68,68,0.08)',
    padding: '8px 16px', borderRadius: 8, width: '100%',
    boxSizing: 'border-box',
  },
  success: {
    color: '#4ade80', fontSize: 12, textAlign: 'center',
    background: 'rgba(74,222,128,0.08)',
    padding: '8px 16px', borderRadius: 8, width: '100%',
    boxSizing: 'border-box',
  },
  link: (accent) => ({
    background: 'none', border: 'none', color: accent,
    fontSize: 12, cursor: 'pointer', padding: 0,
    textDecoration: 'none', fontFamily: "'Inter', sans-serif",
  }),
  muted: {
    background: 'none', border: 'none', color: '#71717a',
    fontSize: 12, cursor: 'pointer', padding: 0,
    fontFamily: "'Inter', sans-serif",
  },
  disclaimer: {
    color: '#3f3f46', fontSize: 10, textAlign: 'center',
    lineHeight: '1.6', maxWidth: 280,
  },
  strengthBar: (pct, color) => ({
    height: 3, borderRadius: 2,
    background: color, width: `${pct}%`,
    transition: 'width 0.3s, background 0.3s',
  }),
  strengthWrap: {
    background: '#27272a', borderRadius: 2, height: 3,
    marginTop: 4, overflow: 'hidden',
  },
  mfaInput: {
    width: '100%', padding: '16px',
    background: 'rgba(24,24,27,0.6)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, color: '#fafafa',
    fontSize: 24, fontWeight: 600,
    textAlign: 'center', letterSpacing: '0.3em',
    outline: 'none', boxSizing: 'border-box',
    fontFamily: 'monospace',
  },
  lockoutBanner: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 10, padding: '12px 16px',
    color: '#f87171', fontSize: 12, textAlign: 'center',
    width: '100%', boxSizing: 'border-box',
  },
  securityBadge: (color) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    color, fontSize: 11, fontWeight: 500,
  }),
};

// ─── Focused input wrapper ───────────────────────────────────────────────────
function AuthInput({ accent, type = 'text', ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      style={{ ...S.input(accent), ...(focused ? S.inputFocus(accent) : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...props}
    />
  );
}

function AuthSelect({ accent, children, ...props }) {
  return (
    <select style={S.select(accent)} {...props}>
      {children}
    </select>
  );
}

// ─── Password strength indicator ─────────────────────────────────────────────
function PasswordStrength({ password }) {
  if (!password) return null;
  const { score, label, color, pct } = checkPasswordStrength(password);
  return (
    <div>
      <div style={S.strengthWrap}>
        <div style={S.strengthBar(pct, color)} />
      </div>
      <p style={{ color, fontSize: 10, margin: '3px 0 0', textAlign: 'right' }}>{label}</p>
    </div>
  );
}

// ─── Lockout countdown ───────────────────────────────────────────────────────
function LockoutBanner({ until }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      if (diff === 0) { setRemaining(''); return; }
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      setRemaining(`${m}:${s.toString().padStart(2, '0')}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);
  if (!remaining) return null;
  return (
    <div style={S.lockoutBanner}>
      🔒 Your account is temporarily locked for security.<br />
      Try again in <strong>{remaining}</strong>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NarbodicaAuth({
  app = 'maia',
  appLabel,
  appTagline,
  accentColor = '#8b5cf6',
  apiBase,
  onSuccess,
  defaultMode = 'login',
  requireDob = true,
  requireGender = true,
  showDisclaimer = false,
  disclaimerText,
}) {
  // Updated 2 May 2026 (Cassiopeia stretch 13) — apiBase is now a required
  // prop. The previous default fallback URL was removed to keep the URL
  // configured in one place per app (src/utils/apiBase.js or src/apiConfig.js)
  // rather than duplicated in this shared library file.
  if (!apiBase) {
    throw new Error('NarbodicaAuth: apiBase prop is required. Pass it from your app\'s utils/apiBase.js or apiConfig.js (no hardcoded fallback).');
  }

  const label = appLabel || app.charAt(0).toUpperCase() + app.slice(1);

  // ── State ──────────────────────────────────────────────────────────────────
  // Check URL for reset token BEFORE initialising state
  const _urlParams = new URLSearchParams(window.location.search);
  const _urlResetToken = _urlParams.get('token') || '';

  const [mode, setMode] = useState(
    _urlResetToken ? 'reset' : (defaultMode === 'reset' ? 'login' : defaultMode)
  ); // login | register | mfa | forgot | forgot_sent | passkey | reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');

  // Password reset
  const [resetToken, setResetToken] = useState(_urlResetToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // MFA
  const [mfaCode, setMfaCode] = useState('');
  const [pasteFeedback, setPasteFeedback] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [mfaMethod, setMfaMethod] = useState('email');

  // Security
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [breachWarning, setBreachWarning] = useState(false);

  // Passkey
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  // Check for a logout reason (e.g. password changed)
  useEffect(() => {
    const reason = sessionStorage.getItem('maia_logout_reason');
    if (reason) {
      setError(reason);
      sessionStorage.removeItem('maia_logout_reason');
    }
  }, []);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeySetupLoading, setPasskeySetupLoading] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null); // { tokens, user } waiting for passkey setup step

  // ── SSO session check — runs once on mount ────────────────────────────────
  // If narb_session cookie is present (set by any Narbodica app login),
  // auto-authenticate without showing the login screen.
  const [checkingSession, setCheckingSession] = useState(!_urlResetToken); // skip if password reset link

  useEffect(() => {
    if (_urlResetToken) return; // don't skip login screen on password reset links
    fetch(`${apiBase}/api/auth/session`, {
      method: 'GET',
      credentials: 'include', // send narb_session cookie cross-origin
      headers: { 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.tokens) {
          // Cookie session valid — auto-login silently
          onSuccess?.(data.data.tokens, data.data.user);
        } else {
          setCheckingSession(false);
        }
      })
      .catch(() => setCheckingSession(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Passkey detection ──────────────────────────────────────────────────────
  useEffect(() => {
    checkPasskeySupport().then(available => {
      setPasskeyAvailable(available);
      // Only auto-switch to passkey if we're on the login screen
      // Don't override reset mode (user clicked a password reset link)
      if (available && !_urlResetToken && defaultMode === 'login') setMode('passkey');
    });
  }, []);

  const clearError = () => { setError(''); setSuccessMsg(''); };

  // ── Age gate ───────────────────────────────────────────────────────────────
  const isUnderage = (dobStr) => {
    if (!dobStr) return false;
    const birth = new Date(dobStr);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age < 18;
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (lockoutUntil && lockoutUntil > Date.now()) return;
    clearError();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    if (!password) { setError('Please enter your password'); return; }
    setLoading(true);
    try {
      const res = await fetch(API(apiBase, '/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.lockoutUntil) setLockoutUntil(new Date(data.lockoutUntil).getTime());
        setError(data.message || 'Sign in failed. Please check your details.');
      } else if (data.mfaRequired || data.requiresMFA) {
        setTempToken(data.tempToken || data.data?.userId);
        setMfaMethod(data.mfaMethod || 'email');
        setMode('mfa');
      } else {
        // Check breach warning (non-blocking)
        checkPasswordBreach(password).then(breached => {
          if (breached) setBreachWarning(true);
        });
        const tokens = data.tokens || data.data?.tokens;
        const user = data.user || data.data?.user;
        const hasPasskey = data.hasPasskey || data.data?.hasPasskey || false;
        // Microsoft-style: if user logged in with password and has no passkey, nudge them
        if (passkeyAvailable && !hasPasskey) {
          setPendingAuth({ tokens, user });
          setMode('passkey_setup');
        } else {
          onSuccess?.(tokens, user);
        }
      }
    } catch {
      setError('Connection error. Please check your internet and try again.');
    }
    setLoading(false);
  };

  // ── Register ───────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    clearError();
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!email.trim()) { setError('Please enter your email address'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (requireDob && !dob) { setError('Please enter your date of birth'); return; }
    if (requireDob && isUnderage(dob)) {
      setError('You must be 18 or older to use this app');
      return;
    }
    if (requireGender && !gender) { setError('Please select your gender'); return; }
    const strength = checkPasswordStrength(password);
    if (strength.score < 2) { setError('Please choose a stronger password — try adding numbers or symbols'); return; }
    setLoading(true);
    try {
      const body = { name: name.trim(), email: email.toLowerCase().trim(), password };
      if (requireDob) body.dateOfBirth = dob;
      if (requireGender) body.gender = gender;
      const res = await fetch(API(apiBase, '/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Registration failed. Please try again.');
      } else if (data.mfaRequired || data.requiresMFA) {
        setTempToken(data.tempToken || data.data?.userId);
        setMfaMethod(data.mfaMethod || 'email');
        setMode('mfa');
      } else {
        const tokens = data.tokens || data.data?.tokens;
        const user = data.user || data.data?.user;
        // Microsoft-style: after register, offer passkey setup before entering app
        if (passkeyAvailable) {
          setPendingAuth({ tokens, user });
          setMode('passkey_setup');
        } else {
          onSuccess?.(tokens, user);
        }
      }
    } catch {
      setError('Connection error. Please check your internet and try again.');
    }
    setLoading(false);
  };

  // ── MFA ────────────────────────────────────────────────────────────────────
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, '').slice(0, 6);
      if (digits.length > 0) {
        setMfaCode(digits);
        setPasteFeedback(true);
        setTimeout(() => setPasteFeedback(false), 1500);
      }
    } catch (e) {
      // Clipboard read failed — user may have denied permission
    }
  };
  const handleMfa = async (e) => {
    e.preventDefault();
    clearError();
    if (mfaCode.length < 6) { setError('Please enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await fetch(API(apiBase, '/auth/verify-mfa'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: tempToken, code: mfaCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Incorrect code. Please try again.');
        setMfaCode('');
      } else {
        const tokens = data.tokens || data.data?.tokens;
        const user = data.user || data.data?.user;
        const hasPasskey = data.hasPasskey || data.data?.hasPasskey || false;
        if (passkeyAvailable && !hasPasskey) {
          setPendingAuth({ tokens, user });
          setMode('passkey_setup');
        } else {
          onSuccess?.(tokens, user);
        }
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  // ── Forgot password ────────────────────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    clearError();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setLoading(true);
    try {
      const res = await fetch(API(apiBase, '/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), app }),  // pass app so reset link goes to correct login screen
      });
      const data = await res.json();
      data.success ? setMode('forgot_sent') : setError(data.message || 'Something went wrong. Please try again.');
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  // ── Reset Password (from email link) ───────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    clearError();
    if (!newPassword) { setError('Please enter a new password'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(API(apiBase, '/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('Password reset successfully! You can now sign in.');
        setNewPassword('');
        setConfirmPassword('');
        setResetToken('');
        // Clean the token from URL
        window.history.replaceState({}, '', window.location.pathname);
        setTimeout(() => { setSuccessMsg(''); setMode('login'); }, 2500);
      } else {
        setError(data.message || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  // ── Passkey ────────────────────────────────────────────────────────────────
  const handlePasskey = async () => {
    setPasskeyLoading(true);
    clearError();
    try {
      const result = await loginWithPasskey(apiBase, email || undefined);
      if (result.success) {
        onSuccess?.(result.tokens, result.user);
      } else if (result.noPasskeys) {
        setError('No passkey found for this device. Sign in with your password to set one up.');
        setMode('login');
      } else {
        setError(result.error || 'Authentication failed. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try signing in with your password.');
      setMode('login');
    }
    setPasskeyLoading(false);
  };

  const backFromMfa = () => { setMfaCode(''); setTempToken(null); setMode('login'); clearError(); };

  const handlePasskeySetup = async () => {
    if (!pendingAuth) return;
    setPasskeySetupLoading(true);
    clearError();
    try {
      const result = await registerPasskey(apiBase, pendingAuth.tokens?.accessToken);
      if (result.success) {
        onSuccess?.(pendingAuth.tokens, pendingAuth.user);
      } else {
        setError(result.error || 'Setup failed — you can set it up later in settings.');
        // Don't block them — let them in anyway after a moment
        setTimeout(() => { onSuccess?.(pendingAuth.tokens, pendingAuth.user); }, 2000);
      }
    } catch {
      onSuccess?.(pendingAuth.tokens, pendingAuth.user);
    }
    setPasskeySetupLoading(false);
  };

  const skipPasskeySetup = () => {
    if (pendingAuth) onSuccess?.(pendingAuth.tokens, pendingAuth.user);
  };
  const accent = accentColor;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — PASSKEY SETUP (post-login/register nudge)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'passkey_setup') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        <div style={{ fontSize: 56, lineHeight: 1 }}>🔐</div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#fafafa', fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>
            Set up Face ID login?
          </p>
          <p style={{ color: '#a1a1aa', fontSize: 13, margin: 0, maxWidth: 260 }}>
            Skip passwords forever. One tap with your face or fingerprint to sign in next time.
          </p>
        </div>
        {error && <p style={S.error}>{error}</p>}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={handlePasskeySetup}
            disabled={passkeySetupLoading}
            style={S.btn(accent, passkeySetupLoading)}
          >
            {passkeySetupLoading ? '🔐 Setting up...' : '✓ Set up now — it takes 2 seconds'}
          </button>
          <button onClick={skipPasskeySetup} style={S.muted}>
            Skip for now
          </button>
        </div>
        <p style={{ color: '#3f3f46', fontSize: 10, textAlign: 'center', maxWidth: 240 }}>
          You can always set this up later in your account settings.
        </p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — MFA
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'mfa') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        <p style={S.tagline}>Enter your verification code</p>
        <p style={{ color: '#a1a1aa', fontSize: 12, textAlign: 'center', margin: 0 }}>
          {mfaMethod === 'email'
            ? `We sent a 6-digit code to ${email}. It expires in 10 minutes.`
            : 'Enter the code from your authenticator app.'}
        </p>
        <form onSubmit={handleMfa} style={S.form}>
          <input
            type="text"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            style={S.mfaInput}
          />
          <button
            type="button"
            onClick={handlePaste}
            style={{
              width: '100%', padding: '10px 16px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, color: pasteFeedback ? '#4ade80' : '#a1a1aa',
              fontSize: 13, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'color 0.2s',
            }}
          >
            {pasteFeedback ? '✓ Code pasted' : '📋 Paste code from clipboard'}
          </button>
          <button type="submit" disabled={loading || mfaCode.length < 6} style={S.btn(accent, loading || mfaCode.length < 6)}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
        {error && <p style={S.error}>{error}</p>}
        <button onClick={backFromMfa} style={S.muted}>← Back to sign in</button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — FORGOT PASSWORD
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'forgot') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        <p style={S.tagline}>Enter your email and we'll send a reset link</p>
        <form onSubmit={handleForgot} style={S.form}>
          <AuthInput accent={accent} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" autoFocus />
          <button type="submit" disabled={loading} style={S.btn(accent, loading)}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        {error && <p style={S.error}>{error}</p>}
        <button onClick={() => { clearError(); setMode(passkeyAvailable ? 'passkey' : 'login'); }} style={S.muted}>← Back to sign in</button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — FORGOT SENT
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'forgot_sent') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        <div style={{ fontSize: 40 }}>✉️</div>
        <p style={{ color: '#a1a1aa', fontSize: 14, textAlign: 'center', margin: 0 }}>
          Check your email for a reset link.<br />It may take a minute to arrive.
        </p>
        <button onClick={() => { clearError(); setMode(passkeyAvailable ? 'passkey' : 'login'); }} style={S.link(accent)}>
          Back to sign in
        </button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — RESET PASSWORD (from email link)
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'reset') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        <p style={S.tagline}>Set your new password</p>
        {successMsg && <p style={{ color: '#4ade80', fontSize: 13, textAlign: 'center', margin: 0 }}>{successMsg}</p>}
        {!successMsg && (
          <form onSubmit={handleReset} style={S.form}>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={S.input(accent)}
              autoFocus
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={S.input(accent)}
            />
            {error && <p style={S.error}>{error}</p>}
            <button type="submit" disabled={loading} style={S.btn(accent, loading)}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
        <button onClick={() => { clearError(); setMode('login'); }} style={S.muted}>
          Back to sign in
        </button>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — PASSKEY
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'passkey') {
    return (
      <div style={S.wrap}>
        <h1 style={S.logo(accent)}>{label}</h1>
        {appTagline && <p style={S.tagline}>{appTagline}</p>}
        <button onClick={handlePasskey} disabled={passkeyLoading} style={S.btn(accent, passkeyLoading)}>
          {passkeyLoading ? '🔐 Authenticating...' : '🔐 Sign in with passkey'}
        </button>
        {error && <p style={S.error}>{error}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <button onClick={() => { clearError(); setMode('login'); }} style={S.muted}>Sign in with password</button>
          <button onClick={() => { clearError(); setMode('register'); }} style={S.link(accent)}>Create an account</button>
        </div>
        {showDisclaimer && (
          <p style={S.disclaimer}>{disclaimerText || `${label} is not a qualified professional. Please seek appropriate professional advice for medical, legal, financial, or mental health matters.`}</p>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER — LOGIN / REGISTER
  // ═══════════════════════════════════════════════════════════════════════════
  const isRegister = mode === 'register';
  // While checking SSO session, show a minimal loader
  if (checkingSession) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100%" }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: accentColor, opacity: 0.4 }} />
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <h1 style={S.logo(accent)}>{label}</h1>
      {appTagline && <p style={S.tagline}>{appTagline}</p>}

      {/* Lockout banner */}
      {lockoutUntil && lockoutUntil > Date.now() && <LockoutBanner until={lockoutUntil} />}

      {/* Breach warning */}
      {breachWarning && (
        <div style={{ ...S.error, background: 'rgba(245,158,11,0.08)', color: '#fbbf24', borderRadius: 8 }}>
          ⚠️ This password was found in a known data breach. We recommend changing it.
        </div>
      )}

      {/* Tabs */}
      <div style={S.tabs}>
        <button style={S.tab(!isRegister, accent)} onClick={() => { clearError(); setMode('login'); }}>Sign In</button>
        <button style={S.tab(isRegister, accent)} onClick={() => { clearError(); setMode('register'); }}>Create Account</button>
      </div>

      <form onSubmit={isRegister ? handleRegister : handleLogin} style={S.form}>
        {isRegister && (
          <AuthInput accent={accent} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoComplete="name" />
        )}
        <AuthInput accent={accent} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" autoComplete="email" />
        <div>
          <AuthInput accent={accent} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" autoComplete={isRegister ? 'new-password' : 'current-password'} />
          {isRegister && <PasswordStrength password={password} />}
        </div>
        {isRegister && requireDob && (
          <div>
            <AuthInput accent={accent} type="date" value={dob} onChange={e => setDob(e.target.value)} max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]} />
            <p style={{ color: '#52525b', fontSize: 10, margin: '3px 0 0 2px' }}>Date of birth — you must be 18 or older</p>
          </div>
        )}
        {isRegister && requireGender && (
          <AuthSelect accent={accent} value={gender} onChange={e => setGender(e.target.value)}>
            <option value="" style={{ background: '#18181b' }}>Select gender</option>
            <option value="male" style={{ background: '#18181b' }}>Male</option>
            <option value="female" style={{ background: '#18181b' }}>Female</option>
            <option value="non-binary" style={{ background: '#18181b' }}>Non-binary</option>
            <option value="other" style={{ background: '#18181b' }}>Other / prefer not to say</option>
          </AuthSelect>
        )}

        {/* APP 5 — Collection notice + consent (register only) */}
        {isRegister && (
          <div style={{ width: '100%', marginTop: 4 }}>
            <p style={{ fontSize: 11, color: '#a1a1aa', lineHeight: '1.5', margin: '0 0 10px 0' }}>
              We collect your name, email, date of birth, and gender to create your account and personalise your experience. 
              {app === 'maia' ? ' Maia will learn from your conversations to provide personalised support. Your messages are processed by Anthropic (USA) for AI responses.' : ''} 
              Payments are processed by Stripe. See our{' '}
              <a href="https://narbodica.com.au/legal" target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'underline' }}>Privacy Policy</a> for full details.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 12, color: '#d4d4d8', lineHeight: '1.5' }}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                style={{ marginTop: 2, accentColor: accent, width: 16, height: 16, flexShrink: 0 }}
              />
              <span>I agree to the <a href="https://narbodica.com.au/legal" target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'underline' }}>Terms of Service</a> and <a href="https://narbodica.com.au/legal" target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'underline' }}>Privacy Policy</a></span>
            </label>
          </div>
        )}

        <button type="submit" disabled={loading || (isRegister && !consentChecked) || (lockoutUntil && lockoutUntil > Date.now())} style={S.btn(accent, loading || (isRegister && !consentChecked) || (lockoutUntil && lockoutUntil > Date.now()))}>
          {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
        </button>
      </form>

      {error && <p style={S.error}>{error}</p>}
      {successMsg && <p style={S.success}>{successMsg}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        {!isRegister && (
          <button onClick={() => { clearError(); setMode('forgot'); }} style={S.muted}>Forgot your password?</button>
        )}
        {!isRegister && passkeyAvailable && (
          <button onClick={() => { clearError(); setMode('passkey'); }} style={S.muted}>Sign in with passkey instead</button>
        )}
      </div>

      {/* Passkey option for register */}
      {isRegister && passkeyAvailable && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          <p style={{ color: '#52525b', fontSize: 11, margin: 0 }}>Already have an account?</p>
          <button onClick={() => { clearError(); setMode('passkey'); }} style={S.btnGhost}>
            🔐 Sign in with passkey
          </button>
        </div>
      )}

      {showDisclaimer && (
        <p style={S.disclaimer}>{disclaimerText || `${label} is not a qualified professional. Please seek appropriate professional advice for medical, legal, financial, or mental health matters.`}</p>
      )}
    </div>
  );
}
